import { ReactNode } from 'react';

import { Box, Button, Dialog, Heading, Icon, Separator, Text } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { SambaAutoLink } from '@betterment/shared-components';

import { trackClick } from '../../../../features/track';

export type ControlSettingsProps = {
  icon: ReactNode;
  title: string;
  name: string;
  subtitle?: string;
  defaultChecked?: boolean;
  detailModal?: ReactNode;
  switchTextHandler: (isChecked: boolean) => string;
};
const SecLendingKey = 'secLending';

const SecLendingDetailModal = () => {
  return (
    <Dialog overlay="default">
      <Dialog.Trigger asChild={true}>
        <Button
          variant="textSecondary"
          css={{
            width: '100%',
            textDecoration: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            textAlign: 'left',
          }}
          onClick={() => trackClick({ name: 'SecLendingModalTrigger' })}
        >
          Earn income by lending securities you own, without selling or giving up investment exposure.
          <Box css={{ display: 'flex', alignItems: 'center' }}>
            <Icon name="CaretRight" size="small" />
          </Box>
        </Button>
      </Dialog.Trigger>
      <Dialog.Content size="large" css={{ display: 'grid', p: 'space.400' }}>
        <Dialog.Title asChild={true}>
          <Heading variant="title400" css={{ mb: 'space.200' }}>
            Securities lending
          </Heading>
        </Dialog.Title>
        <Dialog.Description asChild={true}>
          <Box css={{ display: 'grid', gap: 'space.200' }}>
            <Text>
              Coming in 2026, earn income by lending securities you own, without selling or giving up investment
              exposure.
            </Text>
            <Separator />
            <Box>
              <Heading as="h3" variant="title100">
                How it works
              </Heading>
              <ul className={css({ paddingLeft: 'space.300', display: 'grid', gap: 'space.100' })}>
                <li>Eligible securities from your investing accounts may be lent to other investors.</li>
                <li>
                  Any income you earn from lending goes directly into your account. See{' '}
                  <SambaAutoLink to="http://www.betterment.com/legal/sec-lending-earnings-schedule" target="_blank">
                    schedule
                  </SambaAutoLink>{' '}
                  for details.
                </li>
                <li>Earnings depend on demand for your shares and current lending rates.</li>
                <li>You’re always free to sell lent securities or opt out at any time.</li>
                <li>All shares you lend are fully secured by cash collateral provided by the borrower.</li>
              </ul>
            </Box>
            <Separator />
            <Text variant="text50" color="text.weak">
              The amount you can earn depends on the demand for the securities you own, not all of which may be loaned
              out. Betterment Securities allocates loaned out securities among clients and provides Apex information
              about your account. Instead of dividends, lent securities earn cash substitute payments, which are taxed
              as ordinary income. Betterment Securities’ lending allocation is designed to lend securities when we
              estimate your earnings will exceed incremental tax costs, estimated based on your financial profile.
              Securities on loan are not covered by SIPC but are backed by cash collateral. To learn more, review our{' '}
              <SambaAutoLink to="https://www.betterment.com/help/tag/securities-lending-faqs" target="_blank">
                FAQs
              </SambaAutoLink>{' '}
              and{' '}
              <SambaAutoLink to="http://www.betterment.com/legal/sec-lending-disclosures" target="_blank">
                disclosures.
              </SambaAutoLink>
            </Text>
          </Box>
        </Dialog.Description>
        <Dialog.Close asChild={true}>
          <Box css={{ display: 'flex', mt: 'space.400' }}>
            <Button>Close</Button>
          </Box>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog>
  );
};

export const SecLending = (defaultChecked = true): ControlSettingsProps => {
  return {
    icon: <Icon name="CharityHand" size="small" css={{ flexGrow: 1 }} />,
    title: 'Securities lending',
    subtitle: 'Coming 2026',
    defaultChecked,
    detailModal: <SecLendingDetailModal />,
    switchTextHandler: isChecked => (isChecked ? 'Opt-in' : 'Opt-out'),
    name: SecLendingKey,
  };
};

export const hasEnabledSecLending = (event: React.FormEvent<HTMLFormElement>) => {
  const secLendingSwitch = event.currentTarget.elements.namedItem(SecLendingKey) as Element;
  return secLendingSwitch.getAttribute('value') === 'checked';
};

export const Controls: ControlSettingsProps[] = [SecLending()];
